import React, { useState } from 'react';
import { ReportDoneArea, ReportDoneDepartment, ReportDoneLocation, ReportDoneMonth, ReportDonePercentColor } from '../../types/reportDone5SAudit';

const FALLBACK_COLORS: ReportDonePercentColor[] = [
  { from: 0, to: 80, color: '#FBC0C0', label: '<80%' },
  { from: 80, to: 100, color: '#FDE68A', label: '≥80%' },
  { from: 100, to: 9999, color: '#ACEFC8', label: '≥100%' },
];

const colorForPercent = (value: number, palette: ReportDonePercentColor[]) => {
  const eff = palette.length > 0 ? palette : FALLBACK_COLORS;
  for (const c of eff) {
    if (value >= c.from && value < c.to) return c.color;
  }
  const last = eff[eff.length - 1];
  if (last && value >= last.to) return last.color;
  return 'transparent';
};

const calcPercent = (plan: number, fact: number) => {
  if (plan <= 0) return 0;
  return Math.round((fact / plan) * 100);
};

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width='13'
    height='7'
    viewBox='0 0 13 7'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    style={{ transform: open ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.15s' }}
  >
    <path
      d='M12.0688 6.79375C11.9188 6.79375 11.7938 6.74375 11.6688 6.64375L6.31875 1.36875L0.96875 6.61875C0.74375 6.84375 0.39375 6.84375 0.16875 6.61875C-0.05625 6.39375 -0.05625 6.04375 0.16875 5.81875L5.91875 0.16875C6.14375 -0.05625 6.49375 -0.05625 6.71875 0.16875L12.4688 5.81875C12.6937 6.04375 12.6937 6.39375 12.4688 6.61875C12.3687 6.71875 12.2188 6.79375 12.0688 6.79375Z'
      fill='#9CA3AF'
    />
  </svg>
);

type Props = {
  months: ReportDoneMonth[];
  percentColors: ReportDonePercentColor[];
};

export default function ReportDoneTable({ months, percentColors }: Props) {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const toggle = (key: string) => setOpen((s) => ({ ...s, [key]: !s[key] }));

  const renderPercentCell = (plan: number, fact: number) => {
    const pct = calcPercent(plan, fact);
    return (
      <td className='border border-gray-200 px-3 py-2 text-center font-medium' style={{ backgroundColor: colorForPercent(pct, percentColors) }}>
        {pct}%
      </td>
    );
  };

  const renderArea = (m: ReportDoneMonth, loc: ReportDoneLocation, dep: ReportDoneDepartment, area: ReportDoneArea) => {
    const key = `${m.year}-${m.monthIdx}-${loc.locationId}-${dep.departmentId}-${area.areaId}`;
    const isOpen = !!open[key];
    const hasDetails = area.zones.length > 0 || area.cross;

    const rows: React.ReactNode[] = [
      <tr key={`area-${key}`} className='bg-white'>
        <td className='border border-gray-200 px-3 py-2 text-center'>{m.year}</td>
        <td className='border border-gray-200 px-3 py-2 text-center capitalize'>{m.monthName}</td>
        <td className='border border-gray-200 px-3 py-2 cursor-pointer' style={{ paddingLeft: 48 }} onClick={() => hasDetails && toggle(key)}>
          <span className='flex items-center justify-between'>
            <span>{area.areaName}</span>
            {hasDetails && <Chevron open={isOpen} />}
          </span>
        </td>
        <td className='border border-gray-200 px-3 py-2 text-center'>{area.plan}</td>
        <td className='border border-gray-200 px-3 py-2 text-center'>{area.fact}</td>
        {renderPercentCell(area.plan, area.fact)}
      </tr>,
    ];

    if (isOpen) {
      if (area.cross) {
        rows.push(
          <tr key={`cross-${key}`}>
            <td className='border border-gray-200 px-3 py-2 text-center'>{m.year}</td>
            <td className='border border-gray-200 px-3 py-2 text-center capitalize'>{m.monthName}</td>
            <td className='border border-gray-200 px-3 py-2 text-gray-500' style={{ paddingLeft: 72 }}>Перекрестный аудит</td>
            <td className='border border-gray-200 px-3 py-2 text-center'>{area.cross.plan}</td>
            <td className='border border-gray-200 px-3 py-2 text-center'>{area.cross.fact}</td>
            {renderPercentCell(area.cross.plan, area.cross.fact)}
          </tr>,
        );
      }
      area.zones.forEach((z) => {
        rows.push(
          <tr key={`zone-${key}-${z.zone}`}>
            <td className='border border-gray-200 px-3 py-2 text-center'>{m.year}</td>
            <td className='border border-gray-200 px-3 py-2 text-center capitalize'>{m.monthName}</td>
            <td className='border border-gray-200 px-3 py-2 text-gray-500' style={{ paddingLeft: 72 }}>Внутренний аудит {z.zone}</td>
            <td className='border border-gray-200 px-3 py-2 text-center'>{z.plan}</td>
            <td className='border border-gray-200 px-3 py-2 text-center'>{z.fact}</td>
            {renderPercentCell(z.plan, z.fact)}
          </tr>,
        );
      });
    }
    return rows;
  };

  const renderDepartment = (m: ReportDoneMonth, loc: ReportDoneLocation, dep: ReportDoneDepartment) => {
    const key = `${m.year}-${m.monthIdx}-${loc.locationId}-${dep.departmentId}`;
    const isOpen = !!open[key];
    const hasDetails = dep.areas.length > 0;

    const rows: React.ReactNode[] = [
      <tr key={`dep-${key}`} className='bg-white'>
        <td className='border border-gray-200 px-3 py-2 text-center'>{m.year}</td>
        <td className='border border-gray-200 px-3 py-2 text-center capitalize'>{m.monthName}</td>
        <td className='border border-gray-200 px-3 py-2 cursor-pointer' style={{ paddingLeft: 24 }} onClick={() => hasDetails && toggle(key)}>
          <span className='flex items-center justify-between'>
            <span>{dep.departmentName}</span>
            {hasDetails && <Chevron open={isOpen} />}
          </span>
        </td>
        <td className='border border-gray-200 px-3 py-2 text-center'>{dep.plan}</td>
        <td className='border border-gray-200 px-3 py-2 text-center'>{dep.fact}</td>
        {renderPercentCell(dep.plan, dep.fact)}
      </tr>,
    ];

    if (isOpen) {
      dep.areas.forEach((area) => rows.push(...renderArea(m, loc, dep, area)));
    }
    return rows;
  };

  const renderLocation = (m: ReportDoneMonth, loc: ReportDoneLocation) => {
    const key = `${m.year}-${m.monthIdx}-${loc.locationId}`;
    const isOpen = !!open[key];
    const hasDetails = loc.departments.length > 0;

    const rows: React.ReactNode[] = [
      <tr key={`loc-${key}`} className='bg-white'>
        <td className='border border-gray-200 px-3 py-2 text-center'>{m.year}</td>
        <td className='border border-gray-200 px-3 py-2 text-center capitalize'>{m.monthName}</td>
        <td className='border border-gray-200 px-3 py-2 cursor-pointer font-medium' onClick={() => hasDetails && toggle(key)}>
          <span className='flex items-center justify-between'>
            <span>{loc.locationName}</span>
            {hasDetails && <Chevron open={isOpen} />}
          </span>
        </td>
        <td className='border border-gray-200 px-3 py-2 text-center'>{loc.plan}</td>
        <td className='border border-gray-200 px-3 py-2 text-center'>{loc.fact}</td>
        {renderPercentCell(loc.plan, loc.fact)}
      </tr>,
    ];

    if (isOpen) {
      loc.departments.forEach((dep) => rows.push(...renderDepartment(m, loc, dep)));
    }
    return rows;
  };

  return (
    <table className='w-full border-collapse text-sm'>
      <thead>
        <tr>
          <th className='border border-gray-200 px-3 py-2 w-20 sticky top-0 bg-white z-10'>Год</th>
          <th className='border border-gray-200 px-3 py-2 w-28 sticky top-0 bg-white z-10'>Месяц</th>
          <th className='border border-gray-200 px-3 py-2 text-left sticky top-0 bg-white z-10'>Локация</th>
          <th className='border border-gray-200 px-3 py-2 w-28 sticky top-0 bg-white z-10'>Кол-во аудитов, план</th>
          <th className='border border-gray-200 px-3 py-2 w-28 sticky top-0 bg-white z-10'>Кол-во аудитов, факт</th>
          <th className='border border-gray-200 px-3 py-2 w-20 sticky top-0 bg-white z-10'>%</th>
        </tr>
      </thead>
      <tbody>
        {months.flatMap((m) => m.locations.flatMap((loc) => renderLocation(m, loc)))}
        {months.every((m) => m.locations.length === 0) && (
          <tr>
            <td colSpan={6} className='border border-gray-200 px-3 py-4 text-center text-gray-500'>
              Нет данных за выбранный период
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
